import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface HistoryItem {
  pathname: string;
  search: string;
  href: string;
  title?: string;
  entityType?: string;
  timestamp: number;
}

export const ENTITY_PAGELIST_MAP: Record<string, { to: string; label: string }> = {
  customer: { to: '/customers', label: 'Danh sách khách hàng' },
  product: { to: '/products', label: 'Danh sách sản phẩm' },
  material: { to: '/materials', label: 'Danh sách vật tư' },
  order: { to: '/orders', label: 'Danh sách đơn hàng' },
  production_order: { to: '/production-orders', label: 'Danh sách lệnh sản xuất' },
  quotation: { to: '/quotations', label: 'Danh sách báo giá' },
  plate: { to: '/plates', label: 'Danh sách bản kẽm' },
  mold: { to: '/molds', label: 'Danh sách khuôn mẫu' },
  layout: { to: '/layouts', label: 'Danh sách bài bình' },
  spec: { to: '/specs', label: 'Danh sách thông số KT' },
  design: { to: '/designs', label: 'Danh sách thiết kế' },
  delivery: { to: '/delivery', label: 'Danh sách giao hàng' },
  purchasing: { to: '/purchasing', label: 'Danh sách mua hàng' },
  vendor: { to: '/vendors', label: 'Danh sách nhà cung cấp' },
  qc_check: { to: '/qc-check', label: 'Danh sách KCS' },
  qc_final: { to: '/qc-final', label: 'Danh sách KCS đóng gói' },
  qc_process: { to: '/qc-process', label: 'Danh sách KCS công đoạn' },
  qc_material: { to: '/qc-materials', label: 'Danh sách KCS vật tư' },
  ncr: { to: '/ncr', label: 'Danh sách sự cố KCS' },
  receipt: { to: '/finance/receipts', label: 'Danh sách phiếu thu' },
  ar_ledger: { to: '/finance/ar-ledger', label: 'Sổ cái công nợ phải thu' },
  ap_ledger: { to: '/finance/ap-ledger', label: 'Sổ cái công nợ phải trả' },
  user: { to: '/users', label: 'Danh sách người dùng' },
  role: { to: '/roles', label: 'Danh sách vai trò' },
  process: { to: '/processes', label: 'Danh sách công đoạn' },
  machine: { to: '/machines', label: 'Danh sách máy móc' },
  machine_log: { to: '/machine-logs', label: 'Nhật ký máy móc' },
  feedback: { to: '/feedback', label: 'Danh sách phản hồi' },
};

export function resolveDefaultTitle(pathname: string): string | undefined {
  if (!pathname || pathname === '/') return 'Bàn làm việc (Dashboard)';
  if (pathname === '/dashboard') return 'Bàn làm việc';
  if (pathname === '/reports') return 'Báo cáo quản trị';
  if (pathname.includes('/material-supply')) return 'Bảng cấp vật tư';
  if (pathname.includes('/f0-tracking')) return 'Bảng theo dõi F0';
  if (pathname.includes('/debts')) return 'Sổ công nợ khách hàng';

  // Check create/edit patterns
  if (pathname.endsWith('/create') || pathname.endsWith('/new')) {
    if (pathname.includes('/orders')) return 'Tạo Đơn Hàng';
    if (pathname.includes('/quotations')) return 'Tạo Báo Giá';
    if (pathname.includes('/production-orders')) return 'Lập Lệnh Sản Xuất';
    if (pathname.includes('/products')) return 'Thêm Sản Phẩm';
    if (pathname.includes('/customers')) return 'Thêm Khách Hàng';
    if (pathname.includes('/materials')) return 'Thêm Vật Tư';
    if (pathname.includes('/delivery')) return 'Tạo Phiếu Giao Hàng';
    if (pathname.includes('/purchasing')) return 'Tạo Đơn Mua Hàng';
    return 'Tạo mới';
  }
  if (pathname.endsWith('/edit')) {
    if (pathname.includes('/orders')) return 'Sửa Đơn Hàng';
    if (pathname.includes('/quotations')) return 'Sửa Báo Giá';
    if (pathname.includes('/production-orders')) return 'Sửa Lệnh Sản Xuất';
    if (pathname.includes('/products')) return 'Sửa Sản Phẩm';
    if (pathname.includes('/customers')) return 'Sửa Khách Hàng';
    return 'Chỉnh sửa';
  }

  // Check pagelists
  for (const info of Object.values(ENTITY_PAGELIST_MAP)) {
    if (pathname === info.to) {
      return info.label;
    }
  }

  return undefined;
}

interface NavigationHistoryState {
  stack: HistoryItem[];
  pushRoute: (item: Omit<HistoryItem, 'timestamp'>) => void;
  popRoute: () => HistoryItem | null;
  getPreviousRoute: () => HistoryItem | null;
  getCurrentRoute: () => HistoryItem | null;
  updateCurrentTitle: (title: string, entityType?: string) => void;
  clearHistory: () => void;
}

export const useNavigationHistoryStore = create<NavigationHistoryState>()(
  persist(
    (set, get) => ({
      stack: [],

      pushRoute: (item) => {
        // Skip auth, login, landingpage
        if (
          !item.pathname ||
          item.pathname === '/login' ||
          item.pathname.startsWith('/landingpage')
        ) {
          return;
        }

        const resolvedTitle = item.title || resolveDefaultTitle(item.pathname);

        set((state) => {
          const stack = [...state.stack];
          const last = stack[stack.length - 1];

          // 1. Identical URL: ignore
          if (last && last.href === item.href) {
            return state;
          }

          // 2. Same base pathname (e.g., sub-tab switcher ?tab=... or search/sort query change)
          // Update in-place to avoid trapping user when they press Back
          if (last && last.pathname === item.pathname) {
            stack[stack.length - 1] = {
              ...last,
              search: item.search,
              href: item.href,
              title: resolvedTitle || last.title,
              entityType: item.entityType || last.entityType,
              timestamp: Date.now(),
            };
            return { stack };
          }

          // 3. User is navigating back to the previous entry in stack (Reverse Navigation)
          if (stack.length >= 2 && stack[stack.length - 2].pathname === item.pathname) {
            stack.pop(); // Pop current view
            stack[stack.length - 1] = {
              ...stack[stack.length - 1],
              search: item.search,
              href: item.href,
              title: item.title || stack[stack.length - 1].title,
              entityType: item.entityType || stack[stack.length - 1].entityType,
              timestamp: Date.now(),
            };
            return { stack };
          }

          // 4. Circular loop breaker: if item already exists in recent history (within last 5 items)
          // unwind to that entry to keep the back stack linear and clean
          const existingIndex = stack.slice(-5).findIndex((s) => s.pathname === item.pathname);
          if (existingIndex !== -1) {
            const actualIndex = stack.length - (5 - existingIndex);
            if (actualIndex >= 0 && actualIndex < stack.length) {
              const pruned = stack.slice(0, actualIndex + 1);
              pruned[pruned.length - 1] = {
                ...pruned[pruned.length - 1],
                search: item.search,
                href: item.href,
                title: item.title || pruned[pruned.length - 1].title,
                entityType: item.entityType || pruned[pruned.length - 1].entityType,
                timestamp: Date.now(),
              };
              return { stack: pruned };
            }
          }

          // 5. Standard Forward Navigation
          const newEntry: HistoryItem = {
            ...item,
            title: resolvedTitle,
            timestamp: Date.now(),
          };

          // Limit history depth to 30 items
          if (stack.length >= 30) {
            stack.shift();
          }

          stack.push(newEntry);
          return { stack };
        });
      },

      popRoute: () => {
        let popped: HistoryItem | null = null;
        set((state) => {
          if (state.stack.length <= 1) return state;
          const stack = [...state.stack];
          popped = stack.pop() || null;
          return { stack };
        });
        return popped;
      },

      getPreviousRoute: () => {
        const stack = get().stack;
        if (stack.length <= 1) return null;
        return stack[stack.length - 2];
      },

      getCurrentRoute: () => {
        const stack = get().stack;
        if (stack.length === 0) return null;
        return stack[stack.length - 1];
      },

      updateCurrentTitle: (title: string, entityType?: string) => {
        if (!title || typeof title !== 'string') return;
        set((state) => {
          if (state.stack.length === 0) return state;
          const stack = [...state.stack];
          const lastIdx = stack.length - 1;
          const current = stack[lastIdx];
          if (current.title !== title || (entityType && current.entityType !== entityType)) {
            stack[lastIdx] = {
              ...current,
              title,
              entityType: entityType || current.entityType,
            };
            return { stack };
          }
          return state;
        });
      },

      clearHistory: () => {
        set({ stack: [] });
      },
    }),
    {
      name: 'apv-erp-navigation-history',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
